import { FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp, FaLink } from '@/components/shared/icons';

interface ShareButtonsProps {
    title: string;
    url: string;
    onCopyLink: () => void;
    variant?: "desktop" | "mobile";
    description?: string;
}

/**
 * Generates optimized share messages for social media platforms
 */
const generateShareContent = (title: string, url: string, description?: string) => {
    // Twitter: Concise message with hashtags (280 char limit)
    const twitterText = description
        ? `${title}\n\n${description}\n\n#CadastroImobiliario #Legislacao`
        : `${title}\n\n#CadastroImobiliario #Legislacao`;

    // LinkedIn: Professional tone with context
    const linkedinTitle = `${title} | Cadastro Imobiliário Brasileiro`;

    // WhatsApp: Direct message with title and link
    const whatsappText = `${title}\n\n${url}`;

    return {
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(url)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(linkedinTitle)}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(whatsappText)}`
    };
};

export const ShareButtons = ({
    title,
    url,
    onCopyLink,
    variant = "desktop",
    description
}: ShareButtonsProps) => {
    const shareLinks = generateShareContent(title, url, description);

    if (variant === "mobile") {
        return (
            <div className="md:hidden mb-12 pb-12 border-b border-border">
                <p className="text-sm font-semibold mb-4">Compartilhe esta notícia</p>
                <div className="flex items-center gap-3">
                    <button
                        onClick={onCopyLink}
                        className="flex-1 py-3 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center gap-2"
                        aria-label="Copiar link da notícia"
                    >
                        <FaLink className="w-4 h-4" />
                        <span className="text-sm font-medium">Copiar link</span>
                    </button>
                    <a
                        href={shareLinks.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center group"
                        aria-label="Compartilhar no X (Twitter)"
                        title="Compartilhar no X"
                    >
                        <FaTwitter className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    </a>
                    <a
                        href={shareLinks.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center group"
                        aria-label="Compartilhar no Facebook"
                        title="Compartilhar no Facebook"
                    >
                        <FaFacebook className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    </a>
                    <a
                        href={shareLinks.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center group"
                        aria-label="Compartilhar no WhatsApp"
                        title="Compartilhar no WhatsApp"
                    >
                        <FaWhatsapp className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2">
            <button
                onClick={onCopyLink}
                className="w-10 h-10 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center group"
                aria-label="Copiar link da notícia"
                title="Copiar link"
            >
                <FaLink className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </button>
            <a
                href={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center group"
                aria-label="Compartilhar no X (Twitter)"
                title="Compartilhar no X"
            >
                <FaTwitter className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </a>
            <a
                href={shareLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center group"
                aria-label="Compartilhar no Facebook"
                title="Compartilhar no Facebook"
            >
                <FaFacebook className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </a>
            <a
                href={shareLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center group"
                aria-label="Compartilhar no LinkedIn"
                title="Compartilhar no LinkedIn"
            >
                <FaLinkedin className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </a>
            <a
                href={shareLinks.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-border hover:border-primary hover:bg-muted transition-all flex items-center justify-center group"
                aria-label="Compartilhar no WhatsApp"
                title="Compartilhar no WhatsApp"
            >
                <FaWhatsapp className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </a>
        </div>
    );
};
